<script setup>
import { ref, watch, nextTick, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Btn from './Btn.vue'
import Icon from './Icon.vue'

const props = defineProps({
  user: { type: Object, default: undefined },
  enabled: { type: Boolean, default: true },
  canEdit: { type: Boolean, default: false }
})
const emit = defineEmits(['addEmail'])
const { t } = useI18n()
const visible = ref(false)
const card = ref(null)
let dismissedForVisit = false
let previousFocus
const identity = () => props.user?.id ?? props.user?.username
const storageKey = () => 'email-reminder.snooze.' + identity()

watch(() => [identity(), props.user?.email, props.enabled], ([id, email, enabled], old = []) => {
  if (id !== old[0]) dismissedForVisit = false
  let snoozedUntil = 0
  try {
    if (email) localStorage.removeItem(storageKey())
    else snoozedUntil = Number(localStorage.getItem(storageKey())) || 0
  } catch { /* A reminder must also work when browser storage is unavailable. */ }
  visible.value = !!id && enabled && !email && !dismissedForVisit && Date.now() >= snoozedUntil
}, { immediate: true })

watch(visible, async show => {
  if (show) {
    previousFocus = document.activeElement
    await nextTick()
    if (visible.value) (card.value?.querySelector('[data-primary]') || card.value?.querySelector('button'))?.focus()
  } else {
    previousFocus?.isConnected && previousFocus.focus()
  }
}, { immediate: true })

function later() {
  try { localStorage.setItem(storageKey(), String(Date.now() + 24 * 60 * 60 * 1000)) } catch { /* Keep dismissal for this visit. */ }
  dismissedForVisit = true
  visible.value = false
}
function addEmail() {
  dismissedForVisit = true
  visible.value = false
  emit('addEmail')
}
function keydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); later(); return }
  if (event.key !== 'Tab') return
  const buttons = [...card.value.querySelectorAll('button:not(:disabled)')]
  const first = buttons[0], last = buttons[buttons.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
onUnmounted(() => { if (visible.value && previousFocus?.isConnected) previousFocus.focus() })
</script>

<template>
  <div v-if="visible" class="overlay-backdrop email-reminder-backdrop">
    <section ref="card" class="overlay email-reminder" role="dialog" aria-modal="true" aria-labelledby="email-reminder-title" aria-describedby="email-reminder-description" @keydown="keydown">
      <btn class="email-reminder-close" variant="icon" :aria-label="t('common.Close')" @click="later()"><icon name="close" /></btn>
      <div class="email-reminder-symbol" aria-hidden="true"><icon name="email" /></div>
      <p class="email-reminder-eyebrow">{{ t('users.EmailReminderRecommended') }}</p>
      <h2 id="email-reminder-title">{{ t('users.EmailReminderTitle') }}</h2>
      <p id="email-reminder-description">{{ t('users.EmailReminder') }}</p>
      <p v-if="!canEdit" class="email-reminder-note">{{ t('users.EmailReminderAdmin') }}</p>
      <p class="email-reminder-note">{{ t('users.EmailReminderOptional') }}</p>
      <div class="email-reminder-actions">
        <btn variant="text" @click="later()">{{ t('users.EmailReminderLater') }}</btn>
        <btn v-if="canEdit" color="primary" data-primary @click="addEmail()"><icon name="email" />{{ t('users.AddEmail') }}</btn>
      </div>
      <p class="email-reminder-snooze">{{ t('users.EmailReminderSnooze') }}</p>
    </section>
  </div>
</template>
